import { supabase } from '@/integrations/supabase/client';
import type { EncryptedData, EncryptionResult, DecryptionResult } from '@/types/encryption';
import { isEncryptionHealthy } from './encryption-health-check';

/**
 * Serviço de criptografia para dados sensíveis
 * Utiliza Edge Functions do Supabase para garantir segurança
 */

/**
 * Verifica se a criptografia está habilitada e funcionando
 */
const isEncryptionEnabled = (): boolean => {
  // Verifica se a criptografia passou no health check
  return isEncryptionHealthy();
};

/**
 * Criptografa um campo de texto usando Edge Function
 * SEMPRE tenta criptografar se o valor for válido
 * Lança erro se a criptografia falhar
 */
export const encryptField = async (value: string | number | null | undefined): Promise<EncryptedData> => {
  // Valores vazios retornam dados vazios (permitido)
  if (value === null || value === undefined || value === '') {
    return { encrypted: '', nonce: '' };
  }

  // CRITICAL: Verificar se a criptografia está pronta ANTES de tentar criptografar
  if (!isEncryptionEnabled()) {
    console.error('[Encryption] ❌ Tentativa de criptografar com sistema não inicializado');
    throw new Error('Encryption system not ready - please wait');
  }

  try {
    const stringValue = String(value);
    
    // Get the current session to ensure we have a valid JWT
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('[Encryption] ❌ No active session');
      throw new Error('No active session - please login');
    }
    
    const { data, error } = await supabase.functions.invoke('encrypt-data', {
      body: { dataToEncrypt: stringValue },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });

    if (error) {
      console.error('[Encryption] ❌ Encryption failed:', error.message);
      throw new Error(`Encryption failed: ${error.message}`);
    }

    if (!data || !data.encrypted || !data.nonce) {
      console.error('[Encryption] ❌ Invalid encryption response:', data);
      throw new Error('Invalid encryption response');
    }

    return {
      encrypted: data.encrypted,
      nonce: data.nonce
    };
  } catch (error) {
    console.error('[Encryption] ❌ Encryption operation failed:', error);
    throw error;
  }
};

/**
 * Descriptografa um campo usando Edge Function
 */
export const decryptField = async (data: EncryptedData | null | undefined): Promise<string> => {
  // Se não há dados criptografados, retornar vazio (não é erro)
  if (!data || !data.encrypted || !data.nonce) {
    console.warn('[Encryption] ⚠️ Dados vazios para decrypt - retornando string vazia');
    return '';
  }

  if (!isEncryptionEnabled()) {
    console.error('[Encryption] ❌ Criptografia desabilitada - health check falhou');
    throw new Error('Encryption disabled - health check failed');
  }

  try {
    // Get the current session to ensure we have a valid JWT
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('[Encryption] ❌ No active session for decryption');
      throw new Error('No active session - please login');
    }
    
    const { data: result, error } = await supabase.functions.invoke('decrypt-data', {
      body: { 
        encrypted: data.encrypted,
        nonce: data.nonce 
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });

    if (error) {
      console.error('[Encryption] ❌ Decryption failed:', {
        message: error.message,
        status: error.status,
        encrypted: data.encrypted?.substring(0, 20) + '...',
        nonce: data.nonce?.substring(0, 20) + '...'
      });
      
      // Detectar erro de chave ausente
      if (error.message?.includes('Service unavailable') || error.message?.includes('not configured')) {
        console.error('[Encryption] ❌ ENCRYPTION KEY MISSING: MASTER_ENCRYPTION_KEY não configurada');
        console.error('[Encryption] 📋 Como resolver:');
        console.error('[Encryption] 1. Acesse: https://supabase.com/dashboard/project/ojdktjavsdgcjavmrsyc/settings/functions');
        console.error('[Encryption] 2. Configure o secret "MASTER_ENCRYPTION_KEY"');
        console.error('[Encryption] 3. Recarregue a aplicação');
      }
      
      throw new Error(`Decryption failed: ${error.message}`);
    }

    if (!result || typeof result.decrypted !== 'string') {
      console.error('[Encryption] ❌ Invalid response:', result);
      throw new Error('Invalid decryption response');
    }

    return result.decrypted;
  } catch (error) {
    console.error('[Encryption] ❌ Operation failed:', error);
    throw error; // Re-throw para acionar erro no contexto
  }
};

/**
 * Criptografa múltiplos campos de um objeto
 */
export const encryptObject = async <T extends Record<string, any>>(
  obj: T,
  fields: (keyof T)[]
): Promise<Record<string, any>> => {
  const encrypted: Record<string, any> = { ...obj };

  for (const field of fields) {
    const value = obj[field];
    if (value !== null && value !== undefined) {
      try {
        const encryptedData = await encryptField(value);
        encrypted[`${String(field)}_encrypted`] = encryptedData.encrypted;
        encrypted[`${String(field)}_nonce`] = encryptedData.nonce;
        encrypted[String(field)] = null; // Limpar campo original
      } catch (error) {
        console.error('[Encryption] Field encryption failed');
        throw new Error('Encryption failed');
      }
    }
  }

  return encrypted;
};

/**
 * Descriptografa múltiplos campos de um objeto
 */
export const decryptObject = async <T extends Record<string, any>>(
  obj: T,
  fields: string[]
): Promise<Partial<T>> => {
  const decrypted: Record<string, any> = { ...obj };

  for (const field of fields) {
    const encryptedField = `${field}_encrypted`;
    const nonceField = `${field}_nonce`;

    if (obj[encryptedField as keyof T] && obj[nonceField as keyof T]) {
      try {
        const decryptedValue = await decryptField({
          encrypted: obj[encryptedField],
          nonce: obj[nonceField]
        });
        decrypted[field] = decryptedValue;
      } catch (error) {
        console.error('[Encryption] Field decryption failed');
        // Fallback to original value if exists
        if (obj[field] !== null && obj[field] !== undefined) {
          decrypted[field] = obj[field];
        }
      }
    } else if (obj[field] !== null && obj[field] !== undefined) {
      // Se não tem versão criptografada, usar valor original (período de transição)
      decrypted[field] = obj[field];
    }
  }

  return decrypted as Partial<T>;
};


/**
 * Converte número para string antes de criptografar
 */
export const encryptNumericField = async (value: number | null | undefined): Promise<EncryptedData> => {
  if (value === null || value === undefined) {
    return { encrypted: '', nonce: '' };
  }
  return encryptField(String(value));
};

/**
 * Descriptografa e converte para número
 */
export const decryptNumericField = async (data: EncryptedData | null | undefined): Promise<number> => {
  const decrypted = await decryptField(data);
  return decrypted ? Number(decrypted) : 0;
};
